import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import i18n from '@/utils/i18n';
import {
  EventAccount,
  SendEventMerchantOrderFormLinksValidationError,
} from '../../../../graphql/generatedTypes';
import { useCreateEventAccountMutation } from '../../../../graphql/mutations/createEventAccount.generated';
import { useAddEventAccountToEventParticipantsMutation } from '../../../../graphql/mutations/addEventAccountToEventParticipants.generated';
import { useCreateMerchantOrderSharedLinkMutation } from '../../../../graphql/mutations/createMerchantOrderSharedLink.generated';
import { useSendEventMerchantOrderFormLinksMutation } from '../../../../graphql/mutations/sendEventMerchantOrderFormLinks.generated';
import { GetEventDocument } from '../../../../graphql/queries/getEvent.generated';
import { useAllEventAccountsLazyQuery } from '../../../../graphql/queries/allEventAccounts.generated';
import {
  useGetMerchantOrderSharedLinksForEventCountsQuery,
  GetMerchantOrderSharedLinksForEventCountsDocument,
} from '../../../../graphql/queries/getMerchantOrderSharedLinksForEventCounts.generated';
import { CustomModal } from '../../../../components/CustomModal';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LayoutStoreInstance } from '../../../../LayoutStore';
import { FaPlus, FaLink } from 'react-icons/fa';
import { isDev, isLocalDev } from '@/utils/isDev';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/sonner';
import { LinkCopiedSuccess } from '@/components/LinkCopiedSuccess';
import { MoreHorizontal, Download, AlertTriangle } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { TablePagination } from '@/components/TablePagination';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { Routes } from '../../../../utils/routes';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import { AllowedEventContainerTypes } from '../types';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import SvgIcon from '@/components/Icon/SvgIcon';

type ParticipatingEventAccountsListProps = {
  eventParticipants?: EventAccount[] | undefined | null;
  setEventParticipant?: (e: EventAccount | null) => void;
  eventId?: string;
  eventName?: string | null;
  mainContractingEventAccount?: {
    id: string;
    name?: string | null;
    description?: string | null;
  } | null;
  allowedContainerTypes?: AllowedEventContainerTypes[];
};

const ITEMS_PER_PAGE = 10;

// Helper to make a string filename-safe
const makeFilenameSafe = (str: string): string => {
  return str
    .replace(/[^a-zA-Z0-9-_\s]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 50);
};

type CsvExportRow = {
  event_account_id: string;
  event_account_name: string;
  merchant_order_shared_link_url: string;
};

const ParticipatingEventAccountsList = ({
  eventParticipants,
  setEventParticipant,
  eventId,
  eventName,
  mainContractingEventAccount,
  allowedContainerTypes = [],
}: ParticipatingEventAccountsListProps) => {
  const [selectedEventAccount, setSelectedEventAccount] =
    useState<EventAccount | null>(null);

  // Check if there are no accepted container types
  const hasNoContainerTypes =
    !allowedContainerTypes || allowedContainerTypes.length === 0;

  // Modal state for creating new event account
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [sendOrderFulfillmentEmails, setSendOrderFulfillmentEmails] =
    useState(true);
  const [isBillable, setIsBillable] = useState(true);

  // State for tracking which event account has successfully generated a link
  const [copiedEventAccountId, setCopiedEventAccountId] = useState<
    string | null
  >(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAllPages, setSelectAllPages] = useState(false);

  // Error modal state
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [validationError, setValidationError] =
    useState<SendEventMerchantOrderFormLinksValidationError | null>(null);

  // Item locations error modal state
  const [isItemLocationsErrorModalOpen, setIsItemLocationsErrorModalOpen] =
    useState(false);

  // CSV Export modal state
  const [isCsvExportModalOpen, setIsCsvExportModalOpen] = useState(false);
  const [csvExportProgress, setCsvExportProgress] = useState(0);
  const [csvExportTotal, setCsvExportTotal] = useState(0);
  const [csvExportData, setCsvExportData] = useState<CsvExportRow[]>([]);
  const [csvExportError, setCsvExportError] = useState<string | null>(null);
  const [isCsvExportComplete, setIsCsvExportComplete] = useState(false);

  const [createEventAccount, { loading: createLoading, error: createError }] =
    useCreateEventAccountMutation({
      refetchQueries: [
        {
          query: GetEventDocument,
          variables: { id: eventId },
        },
      ],
    });

  const [addEventAccountToEventParticipants, { loading: addToEventLoading }] =
    useAddEventAccountToEventParticipantsMutation({
      refetchQueries: [
        {
          query: GetEventDocument,
          variables: { id: eventId },
        },
      ],
    });

  // Link existing event account modal state
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkSearchQuery, setLinkSearchQuery] = useState('');
  const [
    fetchEventAccounts,
    { data: allEventAccountsData, loading: allEventAccountsLoading },
  ] = useAllEventAccountsLazyQuery();

  const [createMerchantOrderSharedLink, { loading: createLinkLoading }] =
    useCreateMerchantOrderSharedLinkMutation();

  const [sendEventMerchantOrderFormLinks, { loading: sendLinksLoading }] =
    useSendEventMerchantOrderFormLinksMutation({
      refetchQueries: [
        {
          query: GetEventDocument,
          variables: { id: eventId },
        },
        {
          query: GetMerchantOrderSharedLinksForEventCountsDocument,
          variables: { eventId: eventId || '' },
        },
      ],
    });

  const { data: sharedLinksData, loading: sharedLinksLoading } =
    useGetMerchantOrderSharedLinksForEventCountsQuery({
      variables: { eventId: eventId || '' },
      skip: !eventId,
    });

  // Pagination calculations
  const totalItems = eventParticipants?.length || 0;
  const totalPages = Math.ceil(totalItems / rowsPerPage);

  const paginatedParticipants = useMemo(() => {
    if (!eventParticipants) return [];
    const startIndex = (currentPage - 1) * rowsPerPage;
    return eventParticipants.slice(startIndex, startIndex + rowsPerPage);
  }, [eventParticipants, currentPage, rowsPerPage]);

  // Selection calculations
  const allCurrentPageSelected = useMemo(() => {
    if (paginatedParticipants.length === 0) return false;
    return paginatedParticipants.every((p) => selectedIds.has(p.id));
  }, [paginatedParticipants, selectedIds]);

  const someCurrentPageSelected = useMemo(() => {
    return (
      paginatedParticipants.some((p) => selectedIds.has(p.id)) &&
      !allCurrentPageSelected
    );
  }, [paginatedParticipants, selectedIds, allCurrentPageSelected]);

  const effectiveSelectedCount = selectAllPages ? totalItems : selectedIds.size;

  // Get the selected event accounts for CSV export
  const selectedEventAccounts = useMemo(() => {
    if (!eventParticipants) return [];
    if (selectAllPages) return eventParticipants;
    return eventParticipants.filter((p) => selectedIds.has(p.id));
  }, [eventParticipants, selectedIds, selectAllPages]);

  // Helper to check if an event account has item locations linked to the event
  const hasItemLocationsForEvent = useCallback(
    (eventAccount: EventAccount) => {
      if (!eventId || !eventAccount.item_locations) return false;
      return eventAccount.item_locations.some(
        (loc) => loc.event_id === eventId,
      );
    },
    [eventId],
  );

  // Get selected event accounts without item locations linked to the event
  const selectedAccountsWithoutItemLocations = useMemo(() => {
    return selectedEventAccounts.filter(
      (account) => !hasItemLocationsForEvent(account),
    );
  }, [selectedEventAccounts, hasItemLocationsForEvent]);

  // Calculate counts for each event account
  const getCountsForEventAccount = (eventAccountId: string) => {
    if (
      !sharedLinksData?.getMerchantOrderSharedLinksForEvent
        ?.eventMerchantorderSharedLinks
    ) {
      return { generated: 0, received: 0 };
    }

    const links =
      sharedLinksData.getMerchantOrderSharedLinksForEvent.eventMerchantorderSharedLinks.filter(
        (link) => link.event_account_id === eventAccountId,
      );

    const generated = links.length;
    const received = links.filter(
      (link) => link.merchant_order_id != null,
    ).length;

    return { generated, received };
  };

  useEffect(() => {
    setEventParticipant?.(selectedEventAccount);
  }, [selectedEventAccount, setEventParticipant]);

  useEffect(() => {
    setSelectedEventAccount(null);
    setCopiedEventAccountId(null);
    setCurrentPage(1);
    setSelectedIds(new Set());
    setSelectAllPages(false);
  }, [eventParticipants]);

  // Reset to page 1 when rows per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage]);

  const handleCreateEventAccount = async () => {
    if (!name || !email) {
      alert(i18n.t('EventAccountEditPage:nameAndEmailRequired'));
      return;
    }

    try {
      const result = await createEventAccount({
        variables: {
          input: {
            name,
            description,
            email,
            should_send_order_fulfillment_emails: sendOrderFulfillmentEmails,
            is_billable: isBillable,
            event_id: eventId,
          },
        },
      });

      if (result.data?.createEventAccount) {
        // The new event account is already linked to this event by the backend
        // via the `event_id` passed to createEventAccount above, so no separate
        // addEventAccountToEventParticipants call is needed (a second link would
        // fail with a unique-constraint error on (EventId, EventAccountId)).

        LayoutStoreInstance.setSuccess(
          i18n.t('EventAccountEditPage:eventAccountCreated'),
        );
        // Reset form
        setName('');
        setDescription('');
        setEmail('');
        setSendOrderFulfillmentEmails(true);
        setIsBillable(true);
        setIsCreateModalOpen(false);
      }

      if (result.errors) {
        LayoutStoreInstance.setError(
          i18n.t('EventAccountEditPage:errorCreatingEventAccount'),
        );
      }
    } catch (error) {
      LayoutStoreInstance.setError(
        i18n.t('EventAccountEditPage:errorCreatingEventAccount'),
      );
    }
  };

  const handleGenerateOrderForm = async (eventAccount: EventAccount) => {
    if (!eventId || !eventAccount.id) {
      LayoutStoreInstance.setError(
        i18n.t('Common:error') + ': ' + i18n.t('Events:toastMissingIds'),
      );
      return;
    }

    try {
      const result = await createMerchantOrderSharedLink({
        variables: {
          input: {
            event_account_id: eventAccount.id,
            event_id: eventId,
          },
        },
        refetchQueries: [
          {
            query: GetEventDocument,
            variables: { id: eventId },
          },
          {
            query: GetMerchantOrderSharedLinksForEventCountsDocument,
            variables: { eventId: eventId || '' },
          },
        ],
      });

      if (result.data?.createMerchantOrderSharedLink?.id) {
        const linkId = result.data.createMerchantOrderSharedLink.id;
        const isDevEnvironment = isDev() || isLocalDev();
        const fullLink = `https://partner${isDevEnvironment ? '-dev' : ''}.vytal.org${Routes.eventOrder(linkId)}`;

        // Copy to clipboard
        await navigator.clipboard.writeText(fullLink);

        // Set success state to show the success message instead of button
        setCopiedEventAccountId(eventAccount.id);

        // Show toast with event account name
        toast.success(i18n.t('Common:linkCopied'), {
          description: eventAccount.name
            ? i18n.t('Order:toastFormLinkCopied', { name: eventAccount.name })
            : i18n.t('Order:toastFormLinkCopiedGeneric'),
          action: {
            label: i18n.t('Common:openInNewTab'),
            onClick: () => window.open(fullLink, '_blank'),
          },
        });
      } else {
        LayoutStoreInstance.setError(
          i18n.t('Common:error') +
            ': ' +
            i18n.t('Events:toastGenerateFormError'),
        );
      }
    } catch (error: any) {
      LayoutStoreInstance.setError(
        i18n.t('Common:error') +
          ': ' +
          (error.message || i18n.t('Common:unknownError')),
      );
    }
  };

  const handleToggleCurrentPageSelection = (checked: boolean) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        paginatedParticipants.forEach((p) => newSet.add(p.id));
      } else {
        paginatedParticipants.forEach((p) => newSet.delete(p.id));
      }
      return newSet;
    });
    if (!checked) {
      setSelectAllPages(false);
    }
  };

  const handleToggleRowSelection = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
    if (!checked) {
      setSelectAllPages(false);
    }
  };

  const handleSelectAllAcrossPages = () => {
    if (eventParticipants) {
      setSelectedIds(new Set(eventParticipants.map((p) => p.id)));
      setSelectAllPages(true);
    }
  };

  const handleSendEventOrderLinks = async () => {
    if (!eventId || effectiveSelectedCount === 0) return;

    // Check if any selected event accounts don't have item locations linked to the event
    if (selectedAccountsWithoutItemLocations.length > 0) {
      setIsItemLocationsErrorModalOpen(true);
      return;
    }

    const idsToSend = selectAllPages
      ? eventParticipants?.map((p) => p.id) || []
      : Array.from(selectedIds);

    try {
      const result = await sendEventMerchantOrderFormLinks({
        variables: {
          input: {
            event_id: eventId,
            event_account_ids: idsToSend,
          },
        },
      });

      const response = result.data?.sendEventMerchantOrderFormLinks;

      if (response?.__typename === 'SendEventMerchantOrderFormLinksSuccess') {
        toast.success(
          i18n.t('Event:orderFormsSentSuccess', { count: response.sent_count }),
        );
        // Clear selection
        setSelectedIds(new Set());
        setSelectAllPages(false);
      } else if (
        response?.__typename ===
        'SendEventMerchantOrderFormLinksValidationError'
      ) {
        setValidationError(
          response as SendEventMerchantOrderFormLinksValidationError,
        );
        setIsErrorModalOpen(true);
      }
    } catch (error: any) {
      LayoutStoreInstance.setError(
        i18n.t('Common:error') +
          ': ' +
          (error.message || i18n.t('Common:unknownError')),
      );
    }
  };

  const handleGenerateAndExportCsv = useCallback(async () => {
    if (!eventId || selectedEventAccounts.length === 0) return;

    // Check if any selected event accounts don't have item locations linked to the event
    if (selectedAccountsWithoutItemLocations.length > 0) {
      setIsItemLocationsErrorModalOpen(true);
      return;
    }

    // Reset state and open modal
    setCsvExportProgress(0);
    setCsvExportTotal(selectedEventAccounts.length);
    setCsvExportData([]);
    setCsvExportError(null);
    setIsCsvExportComplete(false);
    setIsCsvExportModalOpen(true);

    const isDevEnvironment = isDev() || isLocalDev();
    const results: CsvExportRow[] = [];

    for (let i = 0; i < selectedEventAccounts.length; i++) {
      const eventAccount = selectedEventAccounts[i];

      try {
        const result = await createMerchantOrderSharedLink({
          variables: {
            input: {
              event_account_id: eventAccount.id,
              event_id: eventId,
            },
          },
        });

        if (result.data?.createMerchantOrderSharedLink?.id) {
          const linkId = result.data.createMerchantOrderSharedLink.id;
          const fullLink = `https://partner${isDevEnvironment ? '-dev' : ''}.vytal.org${Routes.eventOrder(linkId)}`;

          results.push({
            event_account_id: eventAccount.id,
            event_account_name: eventAccount.name || '',
            merchant_order_shared_link_url: fullLink,
          });
        }
      } catch (error: any) {
        setCsvExportError(
          i18n.t('Events:toastLinkGenerationError', {
            name: eventAccount.name || eventAccount.id,
            message: error.message || i18n.t('Common:unknownError'),
          }),
        );
      }

      setCsvExportProgress(i + 1);
    }

    setCsvExportData(results);
    setIsCsvExportComplete(true);
  }, [
    eventId,
    selectedEventAccounts,
    selectedAccountsWithoutItemLocations,
    createMerchantOrderSharedLink,
  ]);

  const handleDownloadCsv = useCallback(() => {
    if (csvExportData.length === 0) return;

    // Create CSV content
    const headers = [
      'event_account_id',
      'event_account_name',
      'merchant_order_shared_link_url',
    ];
    const csvRows = [
      headers.join(','),
      ...csvExportData.map((row) =>
        [
          `"${row.event_account_id}"`,
          `"${row.event_account_name.replace(/"/g, '""')}"`,
          `"${row.merchant_order_shared_link_url}"`,
        ].join(','),
      ),
    ];
    const csvContent = csvRows.join('\n');

    // Create filename
    const safeEventName = makeFilenameSafe(eventName || 'event');
    const safeEventId = makeFilenameSafe(eventId || 'unknown');
    const filename = `order_form_links_${safeEventName}_${safeEventId}.csv`;

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  }, [csvExportData, eventName, eventId]);

  const handleCloseCsvExportModal = useCallback(() => {
    setIsCsvExportModalOpen(false);
    // Refetch to update the counts
    if (isCsvExportComplete && csvExportData.length > 0) {
      // Clear selection after successful export
      setSelectedIds(new Set());
      setSelectAllPages(false);
    }
  }, [isCsvExportComplete, csvExportData.length]);

  const createEventAccountModalBody = (
    <div>
      <div className="tw:mb-4">
        <label
          htmlFor="accountName"
          className="tw:block tw:mb-2 tw:text-sm tw:font-medium"
        >
          {i18n.t('Common:name')}
        </label>
        <input
          type="text"
          name="name"
          id="accountName"
          placeholder={i18n.t('Common:name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-primary"
        />
      </div>
      <div className="tw:mb-4">
        <label
          htmlFor="accountDescription"
          className="tw:block tw:mb-2 tw:text-sm tw:font-medium"
        >
          {i18n.t('Common:description')}
        </label>
        <textarea
          name="description"
          id="accountDescription"
          placeholder={i18n.t('Common:description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-primary"
          rows={4}
        />
      </div>
      <div className="tw:mb-4">
        <label
          htmlFor="accountEmail"
          className="tw:block tw:mb-2 tw:text-sm tw:font-medium"
        >
          {i18n.t('Common:email')}
        </label>
        <input
          type="email"
          name="email"
          id="accountEmail"
          placeholder={i18n.t('Common:email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-primary"
        />
      </div>
      {createError && (
        <div className="tw:mb-4 tw:p-3 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-md tw:text-red-700">
          {i18n.t('Common:error')}: {createError.message}
        </div>
      )}
    </div>
  );

  const isLoading = createLoading || createLinkLoading || sharedLinksLoading;

  // Track if any operation is in progress (for disabling controls)
  const isOperationInProgress =
    sendLinksLoading || (isCsvExportModalOpen && !isCsvExportComplete);

  // Check if all items are selected
  const allItemsSelected =
    effectiveSelectedCount === totalItems && totalItems > 0;

  const handleClearSelection = () => {
    setSelectedIds(new Set());
    setSelectAllPages(false);
  };

  const handleOpenLinkModal = () => {
    setLinkSearchQuery('');
    setIsLinkModalOpen(true);
    fetchEventAccounts();
  };

  const handleLinkEventAccount = async (eventAccountId: string) => {
    if (!eventId) return;
    try {
      await addEventAccountToEventParticipants({
        variables: {
          event_id: eventId,
          event_account_id: eventAccountId,
        },
      });
      LayoutStoreInstance.setSuccess(
        i18n.t('EventAccount:eventAccountLinkedToEvent'),
      );
      setIsLinkModalOpen(false);
    } catch (error: any) {
      LayoutStoreInstance.setError(
        i18n.t('Common:error') + ': ' + (error.message || 'Unknown error'),
      );
    }
  };

  const participantIds = useMemo(
    () => new Set(eventParticipants?.map((p) => p.id) || []),
    [eventParticipants],
  );

  const filteredEventAccounts = useMemo(() => {
    const accounts = allEventAccountsData?.eventAccounts || [];
    const query = linkSearchQuery.toLowerCase().trim();
    return accounts.filter(
      (account) =>
        !participantIds.has(account.id) &&
        (!query || account.name?.toLowerCase().includes(query)),
    );
  }, [allEventAccountsData, linkSearchQuery, participantIds]);

  return (
    <>
      <div className="tw:flex tw:justify-between tw:items-center tw:mb-3">
        <div className="tw:flex tw:flex-col tw:gap-2">
          <h1 className="tw:text-4xl tw:font-semibold">
            {i18n.t('EventAccount:subContractorsParticipatingEventAccounts')}
          </h1>

          <p className="tw:leading-6">
            {i18n.t('Events:eventAccountsDescription')}
          </p>
        </div>
        {eventId && (isDev() || isLocalDev()) && (
          <div className="tw:flex tw:gap-2">
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <FaPlus />
              {i18n.t('EventAccount:createEventAccount')} (only on dev)
            </Button>
            <Button variant="outline" onClick={handleOpenLinkModal}>
              <FaLink />
              Link existing event account (only on dev)
            </Button>
          </div>
        )}
      </div>

      {hasNoContainerTypes && (
        <Alert
          variant="default"
          className="tw:mb-4 tw:bg-yellow-50 tw:border-yellow-200 tw:text-yellow-800"
        >
          <AlertDescription className="tw:ml-2">
            {i18n.t('Events:eventAccountsNoContainerTypes')}
          </AlertDescription>
        </Alert>
      )}

      {eventParticipants && eventParticipants.length === 0 && (
        <p className="tw:text-gray-500 tw:mb-4">
          {i18n.t('Event:noParticipatingEventAccounts')}
        </p>
      )}

      {eventParticipants && eventParticipants.length > 0 && (
        <>
          {/* Selection info - ABOVE the table */}
          <div className="tw:flex tw:items-center tw:justify-between tw:mb-4">
            <div className="tw:flex tw:items-center tw:gap-4">
              <span className="tw:text-sm tw:text-muted-foreground">
                {i18n.t('Event:itemsSelected', {
                  selected: effectiveSelectedCount,
                  total: totalItems,
                })}
              </span>

              <Button
                variant="outline"
                size="sm"
                onClick={
                  allItemsSelected
                    ? handleClearSelection
                    : handleSelectAllAcrossPages
                }
                disabled={isOperationInProgress}
              >
                {allItemsSelected
                  ? i18n.t('Event:clearSelection')
                  : i18n.t('Event:selectAllAcrossPages', {
                      count: totalItems,
                    })}
              </Button>
            </div>

            {effectiveSelectedCount > 0 && (
              <div className="tw:flex tw:items-center tw:gap-2">
                <Button
                  onClick={handleSendEventOrderLinks}
                  disabled={
                    sendLinksLoading ||
                    hasNoContainerTypes ||
                    isOperationInProgress
                  }
                  size="sm"
                >
                  {i18n.t('Event:sendEventOrderLink', {
                    count: effectiveSelectedCount,
                  })}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="tw:h-8 tw:w-8"
                      disabled={hasNoContainerTypes || isOperationInProgress}
                    >
                      <MoreHorizontal className="tw:h-4 tw:w-4" />
                      <span className="tw:sr-only">
                        {i18n.t('Event:actions')}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={handleGenerateAndExportCsv}
                      disabled={hasNoContainerTypes}
                    >
                      <Download className="tw:mr-2 tw:h-4 tw:w-4" />
                      {i18n.t('Event:generateAndExportCsv', {
                        count: effectiveSelectedCount,
                      })}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="tw:w-[50px] tw:pl-4">
                    <Checkbox
                      checked={
                        allCurrentPageSelected
                          ? true
                          : someCurrentPageSelected
                            ? 'indeterminate'
                            : false
                      }
                      onCheckedChange={(checked) =>
                        handleToggleCurrentPageSelection(!!checked)
                      }
                      disabled={isOperationInProgress}
                      aria-label="Select all on current page"
                    />
                  </TableHead>
                  <TableHead className="tw:py-4 tw:text-primary tw:text-sm tw:font-medium">
                    {i18n.t('Event:eventAccountName')}
                  </TableHead>
                  <TableHead className="tw:text-center tw:text-primary tw:text-sm tw:font-medium">
                    {i18n.t('Event:orderFormsGenerated')}
                  </TableHead>
                  <TableHead className="tw:text-center tw:text-primary tw:text-sm tw:font-medium">
                    {i18n.t('Event:orderFormsReceived')}
                  </TableHead>
                  <TableHead className="tw:w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedParticipants.map((eventParticipant) => {
                  const { generated, received } = getCountsForEventAccount(
                    eventParticipant.id,
                  );
                  const isRowSelected = selectedIds.has(eventParticipant.id);
                  return (
                    <TableRow
                      key={eventParticipant.id}
                      data-state={
                        eventParticipant.id === selectedEventAccount?.id
                          ? 'selected'
                          : undefined
                      }
                      className={setEventParticipant ? 'tw:cursor-pointer' : ''}
                      onClick={() => {
                        if (setEventParticipant) {
                          const selected =
                            eventParticipant.id === selectedEventAccount?.id;
                          if (selected) {
                            setSelectedEventAccount(null);
                          } else {
                            setSelectedEventAccount(eventParticipant);
                          }
                        }
                        // Reset copied state when clicking on a different row
                        if (copiedEventAccountId !== eventParticipant.id) {
                          setCopiedEventAccountId(null);
                        }
                      }}
                    >
                      <TableCell className="tw:pl-4">
                        <Checkbox
                          checked={isRowSelected}
                          onCheckedChange={(checked) => {
                            handleToggleRowSelection(
                              eventParticipant.id,
                              !!checked,
                            );
                          }}
                          onClick={(e) => e.stopPropagation()}
                          disabled={isOperationInProgress}
                          aria-label={`Select ${eventParticipant.name}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="tw:flex tw:items-center">
                          <Link
                            to={Routes.eventAccountDetail(eventParticipant.id)}
                            className="tw:text-lilac-600 tw:underline tw:mr-2 tw:hover:text-lilac-700"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {eventParticipant.name}
                          </Link>
                          {mainContractingEventAccount?.id ===
                            eventParticipant.id && (
                            <span className="tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:rounded tw:bg-[#8B5CF6] tw:text-white">
                              {i18n.t('Events:eventAccountsMainBadge')}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="tw:text-center">
                        {sharedLinksLoading ? (
                          <div className="tw:inline-flex tw:items-center tw:justify-center tw:w-8 tw:h-8">
                            <div
                              className="tw:inline-block tw:h-4 tw:w-4 tw:animate-spin tw:rounded-full tw:border-2 tw:border-solid tw:border-current tw:border-r-transparent"
                              role="status"
                            >
                              <span className="tw:sr-only">
                                {i18n.t('Common:loading')}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <Link
                            to={Routes.eventDetailOrders(
                              eventId || '',
                              eventParticipant.id,
                            )}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Badge
                              variant={'secondary'}
                              className={cn(
                                generated === 0 &&
                                  'tw:bg-warning-main tw:outline tw:outline-warning-outline-border tw:text-white',
                              )}
                            >
                              {generated}
                            </Badge>
                          </Link>
                        )}
                      </TableCell>
                      <TableCell className="tw:text-center">
                        {sharedLinksLoading ? (
                          <div className="tw:inline-flex tw:items-center tw:justify-center tw:w-8 tw:h-8">
                            <div
                              className="tw:inline-block tw:h-4 tw:w-4 tw:animate-spin tw:rounded-full tw:border-2 tw:border-solid tw:border-current tw:border-r-transparent"
                              role="status"
                            >
                              <span className="tw:sr-only">
                                {i18n.t('Common:loading')}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <Link
                            to={Routes.eventDetailOrders(
                              eventId || '',
                              eventParticipant.id,
                            )}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Badge
                              variant="secondary"
                              className={cn(
                                received === 0 &&
                                  'tw:bg-warning-main tw:outline tw:outline-warning-outline-border tw:text-white',
                              )}
                            >
                              {received}
                            </Badge>
                          </Link>
                        )}
                      </TableCell>
                      <TableCell className="tw:text-right tw:pr-4">
                        <div className="tw:flex tw:justify-end tw:min-h-[36px] tw:items-center">
                          {copiedEventAccountId === eventParticipant.id ? (
                            <LinkCopiedSuccess />
                          ) : !hasItemLocationsForEvent(eventParticipant) ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="tw:cursor-pointer tw:p-2">
                                  <AlertTriangle className="tw:h-4 tw:w-4 tw:text-warning-main" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                {i18n.t(
                                  'Event:noItemLocationLinkedToEventTooltip',
                                )}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => e.stopPropagation()}
                                  disabled={isOperationInProgress}
                                >
                                  <MoreHorizontal className="tw:h-4 tw:w-4" />
                                  <span className="tw:sr-only">
                                    {i18n.t('Event:actions')}
                                  </span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleGenerateOrderForm(eventParticipant);
                                  }}
                                  disabled={
                                    createLinkLoading || hasNoContainerTypes
                                  }
                                >
                                  <SvgIcon
                                    name="copy-content"
                                    className="tw:mr-2 tw:h-4 tw:w-4"
                                  />
                                  {i18n.t('Event:generateNewOrderFormLink')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>

          {/* Pagination - BELOW the table */}
          {totalPages > 1 && (
            <div className="tw:flex tw:justify-end tw:mt-4">
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                onPageChange={setCurrentPage}
                onRowsPerPageChange={setRowsPerPage}
                rowsPerPageOptions={[6, 10, 25, 50]}
              />
            </div>
          )}
        </>
      )}

      <CustomModal
        isOpen={isCreateModalOpen}
        setIsOpen={setIsCreateModalOpen}
        header={i18n.t('EventAccountEditPage:createNewEventAccount')}
        body={createEventAccountModalBody}
        acceptButtonText={i18n.t('EventAccountEditPage:createEventAccount')}
        acceptButtonIcon={<FaPlus />}
        acceptButtonAction={handleCreateEventAccount}
        acceptButtonDisabled={createLoading || !name || !email}
      />

      {/* Validation Error Modal */}
      <Dialog open={isErrorModalOpen} onOpenChange={setIsErrorModalOpen}>
        <DialogContent className="tw:max-w-lg">
          <DialogHeader>
            <DialogTitle>{i18n.t('Event:sendOrderFormsError')}</DialogTitle>
          </DialogHeader>
          {validationError && (
            <div className="tw:space-y-4">
              <div className="tw:p-3 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-md">
                <p className="tw:text-sm tw:font-medium tw:text-red-800">
                  {i18n.t('Event:errorCode')}: {validationError.error_code}
                </p>
                <p className="tw:text-sm tw:text-red-700 tw:mt-1">
                  {validationError.message}
                </p>
              </div>

              <p className="tw:text-sm tw:text-muted-foreground tw:italic">
                {i18n.t('Event:noEmailsSentNotice')}
              </p>

              {validationError.event_accounts_missing_email &&
                validationError.event_accounts_missing_email.length > 0 && (
                  <div>
                    <h4 className="tw:text-sm tw:font-medium tw:mb-2">
                      {i18n.t('Event:eventAccountsMissingEmail')}:
                    </h4>
                    <ul className="tw:list-disc tw:pl-5 tw:space-y-1">
                      {validationError.event_accounts_missing_email.map(
                        (account) => (
                          <li key={account.event_account_id}>
                            <Link
                              to={Routes.eventAccountDetail(
                                account.event_account_id,
                              )}
                              className="tw:text-lilac-600 tw:underline tw:hover:text-lilac-700"
                              onClick={() => setIsErrorModalOpen(false)}
                            >
                              {account.event_account_name ||
                                account.event_account_id}
                            </Link>
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                )}

              {validationError.invalid_event_account_ids &&
                validationError.invalid_event_account_ids.length > 0 && (
                  <div>
                    <h4 className="tw:text-sm tw:font-medium tw:mb-2">
                      {i18n.t('Event:invalidEventAccountIds')}:
                    </h4>
                    <ul className="tw:list-disc tw:pl-5 tw:space-y-1">
                      {validationError.invalid_event_account_ids.map((id) => (
                        <li key={id} className="tw:text-sm tw:text-gray-700">
                          {id}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              {validationError.not_participating_event_account_ids &&
                validationError.not_participating_event_account_ids.length >
                  0 && (
                  <div>
                    <h4 className="tw:text-sm tw:font-medium tw:mb-2">
                      {i18n.t('Event:notParticipatingEventAccountIds')}:
                    </h4>
                    <ul className="tw:list-disc tw:pl-5 tw:space-y-1">
                      {validationError.not_participating_event_account_ids.map(
                        (id) => (
                          <li key={id} className="tw:text-sm tw:text-gray-700">
                            {id}
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                )}
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary">{i18n.t('Common:close')}</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item Locations Error Modal */}
      <Dialog
        open={isItemLocationsErrorModalOpen}
        onOpenChange={setIsItemLocationsErrorModalOpen}
      >
        <DialogContent className="tw:max-w-lg">
          <DialogHeader>
            <DialogTitle>{i18n.t('Event:sendOrderFormsError')}</DialogTitle>
          </DialogHeader>
          <div className="tw:space-y-4">
            <div className="tw:p-3 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-md">
              <p className="tw:text-sm tw:text-red-700">
                {i18n.t('Event:eventAccountsWithoutItemLocationsError')}
              </p>
            </div>

            <div>
              <h4 className="tw:text-sm tw:font-medium tw:mb-2">
                {i18n.t('Event:eventAccountsWithoutItemLocations')}:
              </h4>
              <ul className="tw:list-disc tw:pl-5 tw:space-y-1">
                {selectedAccountsWithoutItemLocations.map((account) => (
                  <li key={account.id}>
                    <Link
                      to={Routes.eventAccountDetail(account.id)}
                      className="tw:text-lilac-600 tw:underline tw:hover:text-lilac-700"
                      onClick={() => setIsItemLocationsErrorModalOpen(false)}
                    >
                      {account.name || account.id}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary">{i18n.t('Common:close')}</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Existing Event Account Modal */}
      <Dialog open={isLinkModalOpen} onOpenChange={setIsLinkModalOpen}>
        <DialogContent className="tw:max-w-lg">
          <DialogHeader>
            <DialogTitle>Link existing event account</DialogTitle>
          </DialogHeader>
          <div className="tw:space-y-4">
            <Input
              placeholder="Search event accounts..."
              value={linkSearchQuery}
              onChange={(e) => setLinkSearchQuery(e.target.value)}
            />
            <div className="tw:max-h-[300px] tw:overflow-y-auto tw:space-y-1">
              {allEventAccountsLoading ? (
                <p className="tw:text-sm tw:text-muted-foreground tw:py-4 tw:text-center">
                  {i18n.t('Common:loading')}
                </p>
              ) : filteredEventAccounts.length === 0 ? (
                <p className="tw:text-sm tw:text-muted-foreground tw:py-4 tw:text-center">
                  No event accounts found.
                </p>
              ) : (
                filteredEventAccounts.map((account) => (
                  <div
                    key={account.id}
                    className="tw:flex tw:items-center tw:justify-between tw:p-2 tw:rounded-md tw:hover:bg-muted"
                  >
                    <span className="tw:text-sm">{account.name}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleLinkEventAccount(account.id)}
                      disabled={addToEventLoading}
                    >
                      <FaPlus className="tw:mr-1" />
                      Add
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary">{i18n.t('Common:close')}</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Export Progress Modal */}
      <Dialog
        open={isCsvExportModalOpen}
        onOpenChange={(open) => {
          if (!open && isCsvExportComplete) {
            handleCloseCsvExportModal();
          } else if (!open && !isCsvExportComplete) {
            // Don't allow closing while in progress
          } else {
            setIsCsvExportModalOpen(open);
          }
        }}
      >
        <DialogContent className="tw:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {i18n.t('Event:generatingOrderFormLinks')}
            </DialogTitle>
          </DialogHeader>
          <div className="tw:space-y-4">
            {/* Progress indicator */}
            <div className="tw:space-y-2">
              <div className="tw:flex tw:justify-between tw:text-sm">
                <span>{i18n.t('Common:progress')}</span>
                <span>
                  {csvExportProgress} / {csvExportTotal}
                </span>
              </div>
              <div className="tw:w-full tw:bg-gray-200 tw:rounded-full tw:h-2.5">
                <div
                  className="tw:bg-primary tw:h-2.5 tw:rounded-full tw:transition-all tw:duration-300"
                  style={{
                    width: `${csvExportTotal > 0 ? (csvExportProgress / csvExportTotal) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>

            {/* Error message if any */}
            {csvExportError && (
              <div className="tw:p-3 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-md tw:text-red-700 tw:text-sm">
                {csvExportError}
              </div>
            )}

            {/* Success message and download button */}
            {isCsvExportComplete && (
              <div className="tw:space-y-3">
                <p className="tw:text-sm tw:text-green-600">
                  {i18n.t('Event:csvExportComplete', {
                    count: csvExportData.length,
                  })}
                </p>
                <Button
                  onClick={handleDownloadCsv}
                  className="tw:w-full"
                  disabled={csvExportData.length === 0}
                >
                  <Download className="tw:mr-2 tw:h-4 tw:w-4" />
                  {i18n.t('Event:downloadCsv')}
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary" disabled={!isCsvExportComplete}>
                {i18n.t('Common:close')}
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ParticipatingEventAccountsList;
